import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  FaSave, 
  FaMoneyBillWave, 
  FaToggleOn, 
  FaToggleOff, 
  FaShoppingCart, 
  FaLock, 
  FaUnlock,
  FaExclamationTriangle
} from 'react-icons/fa';
import './PaymentSettings.css';

const PaymentSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Загрузка настроек
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        
        // Получаем данные для авторизации
        const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
        
        // Формируем заголовки
        const headers = { 'X-Telegram-ID': telegramId };
        
        const response = await fetch('/api/settings/group/payment', { headers });
        
        if (!response.ok) {
          throw new Error('Ошибка при загрузке настроек');
        }
        
        const data = await response.json();
        
        // Конвертируем массив настроек в объект для удобства
        const settingsObj = data.reduce((acc, setting) => {
          acc[setting.key] = setting;
          return acc;
        }, {});
        
        // Убедимся, что объекты для настроек Prodamus существуют, чтобы поля отображались
        if (!settingsObj.prodamus_domain) {
          settingsObj.prodamus_domain = {
            key: 'prodamus_domain',
            value: '',
            description: 'Поддомен магазина', // Описание можно взять из существующих текстов
            group: 'payment',
            isProtected: false 
          };
        }
        if (!settingsObj.prodamus_secret_key) {
          settingsObj.prodamus_secret_key = {
            key: 'prodamus_secret_key',
            value: '',
            description: 'Секретный ключ Продамус',
            group: 'payment',
            isProtected: true 
          };
        }
        
        setSettings(settingsObj);
        setError(null);
      } catch (err) {
        console.error('Ошибка при загрузке настроек платежей:', err);
        setError('Не удалось загрузить настройки платежей');
      } finally {
        setLoading(false);
      }
    };
    
    if (user) {
      fetchSettings();
    }
  }, [user]);

  // Обработчик изменения значения настройки
  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value
      }
    }));
  };
  
  // Обработчик переключения булевой настройки
  const toggleBooleanSetting = (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: !prev[key].value
      }
    }));
  };

  // Сохранение настроек
  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Получаем данные для авторизации
      const telegramId = user?.telegramId || localStorage.getItem('telegram_id');
      
      // Формируем заголовки
      const headers = { 
        'X-Telegram-ID': telegramId,
        'Content-Type': 'application/json'
      };
      
      // Сохраняем каждую настройку отдельно
      for (const key in settings) {
        const setting = settings[key];
        
        const response = await fetch(`/api/settings/${key}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            value: setting.value,
            description: setting.description,
            group: setting.group,
            isProtected: setting.isProtected
          })
        });
        
        if (!response.ok) {
          throw new Error(`Ошибка при сохранении настройки ${key}`);
        }
      }
      
      setSuccess('Настройки платежей успешно сохранены');
    } catch (err) {
      console.error('Ошибка при сохранении настроек платежей:', err);
      setError('Не удалось сохранить настройки платежей');
    } finally {
      setSaving(false);
      
      // Скрываем сообщение об успехе через 5 секунд
      if (success) {
        setTimeout(() => setSuccess(null), 5000);
      }
    }
  };
  
  // Функция для отображения значений настроек - закомментирована, т.к. не используется
  /* 
  const getDisplayValue = (setting) => {
    if (setting.isProtected && setting.value && setting.value.length > 0) {
      return '••••••••••••••';
    }
    
    if (typeof setting.value === 'boolean') {
      return setting.value ? 'Включено' : 'Выключено';
    }
    
    return setting.value;
  };
  */

  if (loading) {
    return (
      <div className="payment-settings loading">
        <div className="loading-spinner"></div>
        <p>Загрузка настроек платежей...</p>
      </div>
    );
  }

  return (
    <div className="payment-settings">
      <div className="settings-header">
        <h2><FaMoneyBillWave /> Настройки платежных систем</h2>
        <button 
          className="save-button" 
          onClick={saveSettings} 
          disabled={saving}
        >
          <FaSave /> {saving ? 'Сохранение...' : 'Сохранить'}
        </button>
      </div>
      
      {error && (
        <div className="settings-error">
          <FaExclamationTriangle /> {error}
        </div>
      )}
      
      {success && (
        <div className="settings-success">
          {success}
        </div>
      )}
      
      <div className="settings-section">
        <h3>Общие настройки</h3>
        
        {settings.active_payment_provider && (
          <div className="setting-item">
            <div className="setting-label">
              <FaShoppingCart />
              <span>{settings.active_payment_provider.description || 'Активный провайдер платежей'}</span>
            </div>
            <div className="setting-control">
              <select 
                value={settings.active_payment_provider.value}
                onChange={(e) => handleSettingChange('active_payment_provider', e.target.value)}
              >
                <option value="yookassa">ЮKassa</option>
                <option value="prodamus">Продамус</option>
                <option value="manual">Ручное подтверждение</option>
              </select>
            </div>
          </div>
        )}
        
        {settings.allow_manual_payments && (
          <div className="setting-item">
            <div className="setting-label">
              <span>{settings.allow_manual_payments.description || 'Разрешить ручное подтверждение платежей'}</span>
            </div>
            <div className="setting-control toggle">
              <button 
                className={`toggle-button ${settings.allow_manual_payments.value ? 'active' : ''}`}
                onClick={() => toggleBooleanSetting('allow_manual_payments')}
              >
                {settings.allow_manual_payments.value ? 
                  <FaToggleOn className="toggle-icon on" /> : 
                  <FaToggleOff className="toggle-icon off" />
                }
                {settings.allow_manual_payments.value ? 'Включено' : 'Выключено'}
              </button>
            </div>
          </div>
        )}
        

      </div>
      
      <div className="settings-section">
        <h3>Настройки ЮKassa</h3>
        
        <div className="settings-note">
          Эти настройки необходимы для работы с платежной системой ЮKassa. Получите Shop ID и секретный ключ в <a href="https://yookassa.ru/" target="_blank" rel="noopener noreferrer">личном кабинете ЮKassa</a>.
        </div>
        
        {settings.yookassa_shop_id && (
          <div className="setting-item">
            <div className="setting-label">
              {settings.yookassa_shop_id.isProtected ? <FaLock className="lock-icon" /> : <FaUnlock className="lock-icon" />}
              <span>{settings.yookassa_shop_id.description || 'Идентификатор магазина (Shop ID)'}</span>
            </div>
            <div className="setting-control">
              <input 
                type="text" 
                value={settings.yookassa_shop_id.isProtected ? 
                  (settings.yookassa_shop_id.value ? settings.yookassa_shop_id.value : '') : 
                  settings.yookassa_shop_id.value}
                onChange={(e) => handleSettingChange('yookassa_shop_id', e.target.value)}
                placeholder="Введите идентификатор магазина"
              />
            </div>
          </div>
        )}
        
        {settings.yookassa_secret_key && (
          <div className="setting-item">
            <div className="setting-label">
              {settings.yookassa_secret_key.isProtected ? <FaLock className="lock-icon" /> : <FaUnlock className="lock-icon" />}
              <span>{settings.yookassa_secret_key.description || 'Секретный ключ'}</span>
            </div>
            <div className="setting-control">
              <input 
                type={settings.yookassa_secret_key.isProtected ? "password" : "text"}
                value={settings.yookassa_secret_key.value}
                onChange={(e) => handleSettingChange('yookassa_secret_key', e.target.value)}
                placeholder="Введите секретный ключ API"
              />
            </div>
          </div>
        )}
      </div>
      
      <div className="settings-section">
        <h3>Настройки Продамус</h3>
        
        <div className="settings-note">
          Эти настройки необходимы для работы с платежной системой Продамус. Получите Shop ID и секретный ключ в <a href="https://prodamus.ru/" target="_blank" rel="noopener noreferrer">личном кабинете Продамус</a>.
        </div>
        
        {settings.prodamus_domain && (
          <div className="setting-item">
            <div className="setting-label">
              {settings.prodamus_domain.isProtected ? <FaLock className="lock-icon" /> : <FaUnlock className="lock-icon" />}
              <span>{settings.prodamus_domain.description || 'Поддомен магазина'}</span>
            </div>
            <div className="setting-control">
              <input 
                type="text" 
                value={settings.prodamus_domain.isProtected ? 
                  (settings.prodamus_domain.value ? settings.prodamus_domain.value : '') : 
                  settings.prodamus_domain.value}
                onChange={(e) => handleSettingChange('prodamus_domain', e.target.value)}
                placeholder="Введите имя поддомена (например: username)"
              />
              <small className="input-help">.payform.ru</small>
            </div>
          </div>
        )}
        
        {settings.prodamus_secret_key && (
          <div className="setting-item">
            <div className="setting-label">
              {settings.prodamus_secret_key.isProtected ? <FaLock className="lock-icon" /> : <FaUnlock className="lock-icon" />}
              <span>{settings.prodamus_secret_key.description || 'Секретный ключ Продамус'}</span>
            </div>
            <div className="setting-control">
              <input 
                type={settings.prodamus_secret_key.isProtected ? "password" : "text"}
                value={settings.prodamus_secret_key.value}
                onChange={(e) => handleSettingChange('prodamus_secret_key', e.target.value)}
                placeholder="Введите секретный ключ API Продамус"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentSettings;
